/**
 * Machine Learning Feedback System för Yatzy AI
 * Lär sig från användarfeedback (tumme upp/ner) för att förbättra rekommendationer
 */

const IS_DEV = process.env.NODE_ENV === 'development';

export interface GameState {
  dice: number[];
  rollsLeft: number;
  scoreCard: Record<string, number | null>;
  upperSectionSum: number;
  hasBonus: boolean;
  gamePhase: 'early' | 'mid' | 'late';
}

export interface RecommendationFeedback {
  gameState: GameState;
  recommendation: string;
  feedback: 'positive' | 'negative';
  timestamp: number;
  actualChoice?: string; // Vad användaren valde istället
}

export interface LearningPattern {
  pattern: string; // Hash av spelläget
  positiveCount: number;
  negativeCount: number;
  confidence: number;
  lastUpdated: number;
  contexts: GameState[]; // Sparar faktiska spellägen för analys
}

class MLFeedbackSystem {
  private readonly STORAGE_KEY = 'yatzy_ml_feedback';
  private readonly MAX_PATTERNS = 1000; // Begränsa minneanvändning
  private patterns: Map<string, LearningPattern> = new Map();

  constructor() {
    this.loadFromStorage();
  }

  /**
   * Skapar en unik hash för ett spelläge
   */
  private createGameStateHash(gameState: GameState): string {
    // Sorterar tärningar för konsistens
    const sortedDice = [...gameState.dice].sort().join('');
    
    // Identifierar vilka kategorier som är tillgängliga
    const availableCategories = Object.entries(gameState.scoreCard)
      .filter(([_, value]) => value === null)
      .map(([key, _]) => key)
      .sort()
      .join(',');

    // Beräknar bonuspotential
    const upperSum = gameState.upperSectionSum;
    const bonusNeeded = Math.max(0, 63 - upperSum);
    const bonusStatus = gameState.hasBonus ? 'has' : bonusNeeded <= 20 ? 'close' : 'far';

    return `${sortedDice}_${gameState.rollsLeft}_${bonusStatus}_${gameState.gamePhase}_${availableCategories}`;
  }

  /**
   * Registrerar feedback från användaren
   */
  public recordFeedback(
    gameState: GameState,
    recommendation: string,
    feedback: 'positive' | 'negative',
    actualChoice?: string
  ): void {
    const pattern = this.createGameStateHash(gameState);
    
    // Uppdatera eller skapa pattern
    let learningPattern = this.patterns.get(pattern);
    if (!learningPattern) {
      learningPattern = {
        pattern,
        positiveCount: 0,
        negativeCount: 0,
        confidence: 0,
        lastUpdated: Date.now(),
        contexts: []
      };
    }

    // Uppdatera räknare
    if (feedback === 'positive') {
      learningPattern.positiveCount++;
    } else {
      learningPattern.negativeCount++;
    }

    // Beräkna ny confidence (0-1)
    const total = learningPattern.positiveCount + learningPattern.negativeCount;
    learningPattern.confidence = total > 0 ? learningPattern.positiveCount / total : 0;
    learningPattern.lastUpdated = Date.now();

    // Lägg till kontext (begränsa till 5 senaste)
    learningPattern.contexts.push({ ...gameState });
    if (learningPattern.contexts.length > 5) {
      learningPattern.contexts.shift();
    }

    this.patterns.set(pattern, learningPattern);

    // Begränsa antalet patterns för prestanda
    if (this.patterns.size > this.MAX_PATTERNS) {
      this.pruneOldPatterns();
    }

    this.saveToStorage();

    if (IS_DEV) {
      console.log(`[ML] Feedback registrerat: ${feedback} för "${recommendation}" i läge ${pattern}`);
      console.log(`[ML] Ny confidence: ${(learningPattern.confidence * 100).toFixed(1)}% (${learningPattern.positiveCount}/${total})`);
    }
  }

  /**
   * Hämtar confidence för en rekommendation i ett givet spelläge
   */
  public getRecommendationConfidence(gameState: GameState, recommendation: string): number {
    const pattern = this.createGameStateHash(gameState);
    const learningPattern = this.patterns.get(pattern);
    
    if (!learningPattern) {
      return 0.5; // Neutral confidence för nya patterns
    }

    // Justera confidence baserat på antal observationer
    const total = learningPattern.positiveCount + learningPattern.negativeCount;
    const baseConfidence = learningPattern.confidence;
    
    // Minska confidence för få observationer
    if (total < 3) {
      return 0.3 + (baseConfidence * 0.4); // 30-70% range för få samples
    } else if (total < 10) {
      return 0.2 + (baseConfidence * 0.6); // 20-80% range för medel samples
    }
    
    return baseConfidence; // Full confidence för många samples
  }

  /**
   * Hämtar liknande patterns för att ge bättre rekommendationer
   */
  public getSimilarPatterns(gameState: GameState): LearningPattern[] {
    const currentPattern = this.createGameStateHash(gameState);
    const patterns: LearningPattern[] = [];

    this.patterns.forEach((pattern, patternKey) => {
      if (patternKey === currentPattern) return;

      // Kontrollera likhet baserat på viktiga faktorer
      const similarity = this.calculatePatternSimilarity(gameState, pattern.contexts[0]);
      if (similarity > 0.7) { // 70% likhet
        patterns.push(pattern);
      }
    });

    // Sortera efter confidence och senaste uppdatering
    return patterns.sort((a, b) => {
      const scoreA = a.confidence * 0.7 + (a.lastUpdated / Date.now()) * 0.3;
      const scoreB = b.confidence * 0.7 + (b.lastUpdated / Date.now()) * 0.3;
      return scoreB - scoreA;
    });
  }

  /**
   * Beräknar likhet mellan två spellägen
   */
  private calculatePatternSimilarity(state1: GameState, state2: GameState): number {
    if (!state2) return 0;

    let similarity = 0;

    // Tärningslikhet (viktigt)
    const dice1 = [...state1.dice].sort();
    const dice2 = [...state2.dice].sort();
    const diceMatches = dice1.filter((die, i) => die === dice2[i]).length;
    const diceLen = Math.max(dice1.length, 1);
    similarity += (diceMatches / diceLen) * 0.3;

    // Kast kvar
    if (state1.rollsLeft === state2.rollsLeft) similarity += 0.2;

    // Bonusstatus
    if (state1.hasBonus === state2.hasBonus) similarity += 0.15;

    // Spelfas
    if (state1.gamePhase === state2.gamePhase) similarity += 0.15;

    // Tillgängliga kategorier
    const available1 = Object.entries(state1.scoreCard).filter(([_, v]) => v === null).map(([k, _]) => k);
    const available2 = Object.entries(state2.scoreCard).filter(([_, v]) => v === null).map(([k, _]) => k);
    const commonCategories = available1.filter(cat => available2.includes(cat)).length;
    const totalCategories = new Set([...available1, ...available2]).size;
    similarity += totalCategories > 0 ? (commonCategories / totalCategories) * 0.2 : 0.2;

    return similarity;
  }

  /**
   * Rensa gamla patterns för att spara minne
   */
  private pruneOldPatterns(): void {
    const patterns = Array.from(this.patterns.entries());
    
    // Sortera efter senaste uppdatering och confidence
    patterns.sort(([_, a], [__, b]) => {
      const scoreA = a.confidence * 0.7 + (a.lastUpdated / Date.now()) * 0.3;
      const scoreB = b.confidence * 0.7 + (b.lastUpdated / Date.now()) * 0.3;
      return scoreB - scoreA;
    });

    // Behåll endast de bästa 80%
    const keepCount = Math.floor(this.MAX_PATTERNS * 0.8);
    this.patterns.clear();
    
    for (let i = 0; i < keepCount && i < patterns.length; i++) {
      this.patterns.set(patterns[i][0], patterns[i][1]);
    }

    if (IS_DEV) {
      console.log(`[ML] Rensade gamla patterns, behöll ${keepCount} av ${patterns.length}`);
    }
  }

  /**
   * Sparar data till localStorage
   */
  private saveToStorage(): void {
    try {
      const data = Array.from(this.patterns.entries());
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn('[ML] Kunde inte spara till localStorage:', error);
    }
  }

  /**
   * Laddar data från localStorage
   */
  private loadFromStorage(): void {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      if (data) {
        const patterns = JSON.parse(data);
        this.patterns = new Map(patterns);
        if (IS_DEV) {
          console.log(`[ML] Laddade ${this.patterns.size} inlärda patterns från localStorage`);
        }
      }
    } catch (error) {
      console.warn('[ML] Kunde inte ladda från localStorage:', error);
      this.patterns = new Map();
    }
  }

  /**
   * Hämtar statistik om inlärningssystemet
   */
  public getStats(): {
    totalPatterns: number;
    totalFeedback: number;
    averageConfidence: number;
    recentActivity: number;
  } {
    const now = Date.now();
    const oneDayAgo = now - (24 * 60 * 60 * 1000);
    
    let totalFeedback = 0;
    let totalConfidence = 0;
    let recentActivity = 0;

    this.patterns.forEach((pattern) => {
      const feedback = pattern.positiveCount + pattern.negativeCount;
      totalFeedback += feedback;
      totalConfidence += pattern.confidence;
      
      if (pattern.lastUpdated > oneDayAgo) {
        recentActivity++;
      }
    });

    return {
      totalPatterns: this.patterns.size,
      totalFeedback,
      averageConfidence: this.patterns.size > 0 ? totalConfidence / this.patterns.size : 0,
      recentActivity
    };
  }

  /**
   * Återställer all inlärningsdata
   */
  public reset(): void {
    this.patterns.clear();
    localStorage.removeItem(this.STORAGE_KEY);
    console.log('[ML] Återställde all inlärningsdata');
  }
}

// Singleton instance
export const mlFeedbackSystem = new MLFeedbackSystem();
